import { DatesRange, UseState } from '@elearning-platform/ui';
import dayjs from 'dayjs';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { SetURLSearchParams, useSearchParams } from 'react-router-dom';
import { DebouncedState, useDebounce } from 'use-debounce';

import { ViewSearchParam } from '../../../enums';
import { FilterName } from '../enums';
import { UseFilters } from '../interfaces';
import { TrainingFilters } from '../types';

export const useFilters: (allowedCategories: string[], allowedTypes: string[]) => UseFilters
= (allowedCategories: string[], allowedTypes: string[]): UseFilters => {
  const [params, setParams]: [URLSearchParams, SetURLSearchParams] = useSearchParams();
  const urlSearchValue: string = params.get(ViewSearchParam.TrainingSearch) ?? '';
  const [localSearch, setLocalSearch]: UseState<string> = useState<string>(urlSearchValue);
  const [debouncedSearch]: [string, DebouncedState<(value: string) => void>] = useDebounce(localSearch, 200);
  const paramsString: string = params.toString();

  const parseUnix: (value: string | null) => number | null = (value: string | null): number | null => {
    if (!value) {
      return null;
    }
    const timestamp: number = Number(value);

    return Number.isInteger(timestamp) && timestamp > 0 ? timestamp : null;
  };

  const filters: TrainingFilters = useMemo((): TrainingFilters => {
    const current: URLSearchParams = new URLSearchParams(paramsString);
    const result: TrainingFilters = {};

    const rawCategories: string[] = current.getAll(ViewSearchParam.TrainingCategory);
    const categories: string[] = allowedCategories
      ? rawCategories.filter((category: string): boolean => allowedCategories.includes(category))
      : rawCategories;
    if (categories.length) {
      result[FilterName.Category] = categories;
    }

    const rawTypes: string[] = current.getAll(ViewSearchParam.TrainingType);
    const types: string[] = allowedTypes
      ? rawTypes.filter((type: string): boolean => allowedTypes.includes(type))
      : rawTypes;
    if (types.length) {
      result[FilterName.Type] = types;
    }

    const dateFrom: number | null = parseUnix(current.get(ViewSearchParam.TrainingFrom));
    const dateTo: number | null = parseUnix(current.get(ViewSearchParam.TrainingTo));

    if (dateFrom !== null || dateTo !== null) {
      result[FilterName.DatesRange] = {
        ...(dateFrom !== null && { dateFrom: dayjs.unix(dateFrom) }),
        ...(dateTo !== null && { dateTo: dayjs.unix(dateTo) }),
      };
    }

    return result;
  }, [paramsString, allowedCategories, allowedTypes]);

  useEffect((): void => {
    setLocalSearch(urlSearchValue);
  }, [urlSearchValue]);

  useEffect((): void => {
    if (debouncedSearch === urlSearchValue) {
      return;
    }

    setParams((prev: URLSearchParams): URLSearchParams => {
      const updated: URLSearchParams = new URLSearchParams(prev);

      if (debouncedSearch) {
        updated.set(ViewSearchParam.TrainingSearch, debouncedSearch);
      } else {
        updated.delete(ViewSearchParam.TrainingSearch);
      }

      return updated;
    }, { replace: true });
  }, [debouncedSearch, urlSearchValue, setParams]);

  const setFilters: (next: TrainingFilters) => void = useCallback((next: TrainingFilters): void => {
    setParams((prev: URLSearchParams): URLSearchParams => {
      const updated: URLSearchParams = new URLSearchParams(prev);

      updated.delete(ViewSearchParam.TrainingCategory);
      updated.delete(ViewSearchParam.TrainingType);
      updated.delete(ViewSearchParam.TrainingFrom);
      updated.delete(ViewSearchParam.TrainingTo);

      if (Array.isArray(next[FilterName.Category])) {
        next[FilterName.Category].forEach((value: string): void => updated.append(ViewSearchParam.TrainingCategory, value));
      }

      if (Array.isArray(next[FilterName.Type])) {
        next[FilterName.Type].forEach((value: string): void => updated.append(ViewSearchParam.TrainingType, value));
      }

      const dates: string[] | DatesRange | undefined = next[FilterName.DatesRange];
      if (dates && !Array.isArray(dates)) {
        if (dates.dateFrom) {
          updated.set(ViewSearchParam.TrainingFrom, String(dates.dateFrom.unix()));
        }
        if (dates.dateTo) {
          updated.set(ViewSearchParam.TrainingTo, String(dates.dateTo.unix()));
        }
      }

      return updated;
    }, { replace: true });
  }, [setParams]);

  return {
    filters,
    setFilters,
    setSearchValue: setLocalSearch,
    searchValue: localSearch,
    appliedSearchValue: debouncedSearch,
  };
};
